/**
 * Alpine component for the participant offline experience.
 *
 *   - `online` — reactive boolean reflecting `navigator.onLine`
 *   - `pending` — count of answers queued in localStorage awaiting sync
 *   - `enqueue(payload)` — store an answer locally; pending increments
 *   - `flush()` — POST every queued answer to /s/{code}/answer; remove on
 *     2xx (accepted | duplicate). Auto-fires on the `online` event.
 *   - `submitOrQueue({sessionCode, body})` — UI helper used by the participant
 *     view: try fetch, fall back to queue. Returns {synced} | {synced, queued}.
 *
 * Storage shape: array of { sessionCode, body, queuedAt } objects under one key.
 * Each `body` carries its own `idempotency_key`, so retries don't double-count.
 */
const STORAGE_KEY = 'pulsar.answer.queue.v1';

function readQueue() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function writeQueue(queue) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch {
        // Storage full / private mode — best effort.
    }
}

export default function offlineQueue() {
    return {
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        pending: 0,

        init() {
            this.pending = readQueue().length;

            window.addEventListener('online', () => {
                this.online = true;
                this.flush();
            });
            window.addEventListener('offline', () => {
                this.online = false;
            });

            // Drain anything left over from a previous tab/session on first load.
            if (this.online && this.pending > 0) {
                this.flush();
            }
        },

        enqueue({ sessionCode, body }) {
            const queue = readQueue();
            queue.push({ sessionCode, body, queuedAt: Date.now() });
            writeQueue(queue);
            this.pending = queue.length;
        },

        async flush() {
            const queue = readQueue();
            if (queue.length === 0) {
                this.pending = 0;
                return;
            }

            const remaining = [];
            for (const item of queue) {
                try {
                    const response = await fetch(`/s/${item.sessionCode}/answer`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(item.body),
                    });
                    if (response.ok || response.status === 200 || response.status === 201) {
                        // 2xx -> drop (success or duplicate)
                        continue;
                    }
                    if (response.status === 429 || response.status >= 500) {
                        // Transient — retry with backoff.
                        remaining.push(item);
                    }
                    // 4xx other than 429 (validation, forbidden, not-found) —
                    // unrecoverable: drop to avoid an infinite loop.
                } catch {
                    // Network error — keep in queue for the next online tick.
                    remaining.push(item);
                }
            }

            writeQueue(remaining);
            this.pending = remaining.length;

            if (remaining.length > 0 && this.online) {
                // Some items failed for non-network reasons — back off briefly
                // before retrying so we don't busy-loop.
                setTimeout(() => this.flush(), 5_000);
            }
        },

        async submitOrQueue({ sessionCode, body }) {
            if (this.online) {
                try {
                    const response = await fetch(`/s/${sessionCode}/answer`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(body),
                    });
                    if (response.ok) {
                        return { synced: true };
                    }
                } catch {
                    // Fall through to queue.
                }
            }
            this.enqueue({ sessionCode, body });
            return { synced: false, queued: true };
        },
    };
}
